import { IProgressGPTChatModel } from 'api/models';
import clsx from 'clsx';
import { Divider } from 'library/divider';
import { Dropdown, DropdownItem, DropdownList, DropdownTrigger } from 'library/dropdown';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    EllipsisVerticalIcon,
    HamburgerMenuIcon,
    PenToSquareIcon,
} from 'library/icons';
import { useDesktopMediaQuery } from 'library/media-query';
import { OverflowTooltip } from 'library/tooltip';
import groupBy from 'lodash.groupby';
import EventBlocker from 'modules/shared/components/helpers/EventBlocker';
import useAuthSelector from 'modules/shared/hooks/useAuthSelector';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import ProgressGPTBarLoading from '../ProgressGPTBarLoading/ProgressGPTBarLoading';

interface IProgressGPTbarProps {
    chatId: string;
    chats: IProgressGPTChatModel[];
    isChatsLoading: boolean;
    onNewChat: () => void;
    onClick: (chatId: string) => void;
    onDelete: (chat: IProgressGPTChatModel) => void;
}

const ProgressGPTBar = (props: IProgressGPTbarProps) => {
    const { chatId, chats, isChatsLoading, onNewChat, onClick, onDelete } = props;

    const { user } = useAuthSelector();

    const isDesktop = useDesktopMediaQuery();

    const [isOpen, setIsOpen] = useState(true);

    useEffect(() => {
        if (!isDesktop) {
            setIsOpen(false);
        }
    }, [isDesktop]);
    const groupedChats = useMemo(() => {
        // First, sort all chats by creation date (newest first)
        const sortedChats = [...chats].sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf());

        const chatMap = groupBy(sortedChats, (item) => {
            const date = moment.tz(item.createdAt, user.preferences.timezone);
            const today = moment.tz(user.preferences.timezone).startOf('day');

            if (date.isSame(today, 'day')) {
                return 'Today';
            } else {
                return 'Older';
            }
        });

        const result = [];
        if (chatMap['Today']) {
            result.push({ date: 'Today', chats: chatMap['Today'] });
        }
        if (chatMap['Older']) {
            result.push({ date: 'Older', chats: chatMap['Older'] });
        }

        return result;
    }, [chats, user.preferences.timezone]);

    // const groupedChat = useMemo(() => {
    //     const chatMap = groupBy(chats, (item) => {
    //         const date = moment.tz(item.createdAt, user.preferences.timezone);
    //         const today = moment.tz(user.preferences.timezone).startOf('day');

    //         if (date.isSame(today, 'day')) {
    //             return 'Today';
    //         } else {
    //             return 'Older';
    //         }
    //     });

    //     const result = [];
    //     if (chatMap['Today']) {
    //         result.push({ date: 'Today', chats: chatMap['Today'] });
    //     }
    //     if (chatMap['Older']) {
    //         result.push({ date: 'Older', chats: chatMap['Older'] });
    //     }

    //     return result;
    // }, [chats, user.preferences.timezone]);

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    if (!isOpen) {
        return (
            <div
                className={clsx(
                    'relative py-4 z-50 transition-all duration-300 ease-in-out',
                    isDesktop
                        ? 'flex flex-col items-center justify-start  bg-card-color w-[60px] h-screen border-r border-separator-color-light  shadow-[4px_0_20px_-4px_rgba(0,0,0,0.1)] dark:shadow-[4px_0_20px_-4px_rgba(0,0,0,0.3)]'
                        : ''
                )}
            >
                {isDesktop ? (
                    <>
                        <div className="relative btn-action">
                            <button onClick={handleToggle} className=" text-grayscale-500  cursor-pointer">
                                <ChevronRightIcon width={10} height={13} strokeWidth={1.25} />
                            </button>
                        </div>
                        <Divider variant="light" />
                        <button type="button" className="mt-6 text-primary cursor-pointer" onClick={onNewChat}>
                            <PenToSquareIcon />
                        </button>
                    </>
                ) : (
                    <div className="absolute left-5" onClick={handleToggle}>
                        <HamburgerMenuIcon />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            className={clsx(
                'relative bg-card-color overflow-y-hidden h-screen border-r border-separator-color-light flex flex-col transition-all duration-300 ease-in-out py-4 px-5 shadow-[4px_0_20px_-4px_rgba(0,0,0,0.1)] dark:shadow-[4px_0_20px_-4px_rgba(0,0,0,0.3)] z-50 overflow-hidden',
                isDesktop ? 'w-[340px]' : 'w-[280px]'
            )}
        >
            <div className="flex items-center justify-between pb-4 flex-shrink-0">
                <button
                    type="button"
                    className="flex items-center gap-3 cursor-pointer text-primary"
                    onClick={() => {
                        onNewChat();
                        if (!isDesktop) {
                            setIsOpen(false);
                        }
                    }}
                >
                    <PenToSquareIcon />
                    <span className="text-sm font-medium">New Chat</span>
                </button>

                <button onClick={handleToggle} className="relative btn-action text-grayscale-500 cursor-pointer">
                    <ChevronLeftIcon width={10} height={13} strokeWidth={1.25} />
                </button>
            </div>

            <Divider variant="light" />

            <div className="flex-1 overflow-y-auto hide-scrollbar">
                {isChatsLoading && <ProgressGPTBarLoading />}

                {chats &&
                    groupedChats.map((row) => (
                        <div key={row.date} className="mt-4">
                            <div className="text-grayscale-500 text-xs px-2 font-medium mb-4">{row.date}</div>

                            {row.chats.map((chat) => (
                                <div
                                    key={chat._id}
                                    className="text-sm font-medium text-grayscale-500  cursor-pointer mt-2"
                                >
                                    <div
                                        className={clsx(
                                            'flex items-center justify-between gap-6 hover:bg-grayscale-100 px-2 py-1 rounded-md cursor-pointer',
                                            chat._id === chatId && 'bg-grayscale-100'
                                        )}
                                        onClick={() => {
                                            onClick(chat._id);
                                            if (!isDesktop) {
                                                setIsOpen(false);
                                            }
                                        }}
                                    >
                                        <OverflowTooltip interactive={false} contentClassName="max-w-[300px]">
                                            {chat.title}
                                        </OverflowTooltip>
                                        <EventBlocker stopPropagation preventDefault>
                                            <Dropdown>
                                                <DropdownTrigger className="w-6 h-6 grid place-items-center">
                                                    <EllipsisVerticalIcon className="text-grayscale-500" />
                                                </DropdownTrigger>
                                                <DropdownList>
                                                    <DropdownItem
                                                        className="text-danger-500"
                                                        onClick={() => onDelete(chat)}
                                                    >
                                                        Delete
                                                    </DropdownItem>
                                                </DropdownList>
                                            </Dropdown>
                                        </EventBlocker>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
            </div>
        </div>
    );
};

export default ProgressGPTBar;
